import { Scan } from '../models/scan.model';

/** Canonical display order for status values (matches the CSV `Status`). */
export const STATUS_ORDER = ['Completed', 'Exception Reported', 'Abandoned', 'Unsuccessful'];

/** Fixed colors per status, shared across all charts. */
export const STATUS_COLORS: Record<string, string> = {
  'Completed': '#00B050',          // green
  'Exception Reported': '#0070C0', // blue
  'Abandoned': '#C00000',          // red
  'Unsuccessful': '#FFC000'        // amber/yellow
};

export function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? '#9e9e9e'; // grey fallback
}

/** Rotating palette for categorical (non-status) chart series, e.g. sites. */
export const CATEGORY_PALETTE = [
  '#1976d2', '#43a047', '#fb8c00', '#e53935', '#8e24aa', '#00897b',
  '#3949ab', '#fdd835', '#d81b60', '#00acc1', '#6d4c41', '#7cb342'
];

export function categoryColor(index: number): string {
  return CATEGORY_PALETTE[index % CATEGORY_PALETTE.length];
}

export interface StatusCount {
  status: string;
  count: number;
}

export interface StatusByDay {
  days: string[];
  series: { status: string; data: number[] }[];
}

/** Count scans per status (descending), using the given status accessor. */
export function aggregateStatusCounts(scans: Scan[], accessor: (s: Scan) => string): StatusCount[] {
  const counts = new Map<string, number>();
  for (const scan of scans) {
    const status = accessor(scan);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => b.count - a.count);
}

/** Per-day status counts (by startDate) shaped for a stacked bar chart. */
export function aggregateStatusByDay(scans: Scan[], accessor: (s: Scan) => string): StatusByDay {
  const byDay = new Map<string, Map<string, number>>();
  const statusSet = new Set<string>();

  for (const scan of scans) {
    const day = scan.activityAndSessionDetails.startDate.slice(0, 10); // YYYY-MM-DD
    const status = accessor(scan);
    statusSet.add(status);
    const dayMap = byDay.get(day) ?? new Map<string, number>();
    dayMap.set(status, (dayMap.get(status) ?? 0) + 1);
    byDay.set(day, dayMap);
  }

  const days = [...byDay.keys()].sort();
  const statuses = [...statusSet].sort();
  const series = statuses.map(status => ({
    status,
    data: days.map(day => byDay.get(day)?.get(status) ?? 0)
  }));

  return { days, series };
}

export interface SiteActivity {
  site: string;
  count: number;
  lat: number;
  lng: number;
  city: string;
  state: string;
}

/**
 * Scan counts per site with their GPS coordinates, for plotting on a map.
 * Sites with an unparseable gpsLatLong are dropped.
 */
export function aggregateSiteActivity(scans: Scan[]): SiteActivity[] {
  const bySite = new Map<string, SiteActivity>();
  for (const scan of scans) {
    const s = scan.siteAndLocationAttributes;
    let entry = bySite.get(s.userSiteName);
    if (!entry) {
      const [lat, lng] = (s.gpsLatLong ?? '').split(',').map(v => Number(v.trim()));
      entry = { site: s.userSiteName, count: 0, lat, lng, city: s.city, state: s.state };
      bySite.set(s.userSiteName, entry);
    }
    entry.count++;
  }
  return [...bySite.values()].filter(e => Number.isFinite(e.lat) && Number.isFinite(e.lng));
}

export interface StatusByCategory {
  categories: string[];
  series: { status: string; data: number[] }[];
}

/**
 * Status counts grouped by an arbitrary category (e.g. site), shaped for a
 * stacked bar chart: `categories` are the x-axis, one series per status.
 */
export function aggregateStatusByCategory(
  scans: Scan[],
  category: (s: Scan) => string,
  status: (s: Scan) => string
): StatusByCategory {
  const byCategory = new Map<string, Map<string, number>>();
  const statusSet = new Set<string>();

  for (const scan of scans) {
    const cat = category(scan);
    const st = status(scan);
    statusSet.add(st);
    const map = byCategory.get(cat) ?? new Map<string, number>();
    map.set(st, (map.get(st) ?? 0) + 1);
    byCategory.set(cat, map);
  }

  const categories = [...byCategory.keys()].sort();
  const statuses = [...statusSet].sort();
  const series = statuses.map(status => ({
    status,
    data: categories.map(c => byCategory.get(c)?.get(status) ?? 0)
  }));

  return { categories, series };
}
